/**
 * @file InactivityRedirectModal.tsx
 *
 * @description A modal component that displays when a user has been redirected due to inactivity.
 * This modal requires explicit acknowledgment before the user can proceed, ensuring the message is read.
 *
 * @module Modals
 * @requires React
 * @requires Material-UI
 */

import React from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Fade,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

/**
 * Props for the InactivityRedirectModal component
 * @interface InactivityRedirectModalProps
 * @property {boolean} open - Controls whether the modal is displayed
 * @property {() => void} onAcknowledge - Callback function triggered when the user acknowledges the message
 * @property {string} [message] - Optional custom message to display
 */
interface InactivityRedirectModalProps {
  open: boolean;
  onAcknowledge: () => void;
  message?: string;
}

/**
 * @name InactivityRedirectModal
 *
 * @description A modal dialog that informs the user they were redirected due to inactivity.
 * The user must click the acknowledge button to dismiss the modal and continue using the application.
 * This ensures important session timeout messages are not missed.
 *
 * @param {InactivityRedirectModalProps} props - Component props
 *
 * @returns {React.ReactElement} The rendered inactivity redirect modal
 */
const InactivityRedirectModal: React.FC<InactivityRedirectModalProps> = ({
  open,
  onAcknowledge,
  message = "You were redirected due to inactivity.",
}) => {
  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      onClose={(_, reason) => {
        // Prevent closing by clicking outside the modal
        if (reason === "backdropClick") {
          return;
        }
      }}
      aria-labelledby="inactivity-redirect-dialog-title"
      aria-describedby="inactivity-redirect-dialog-description"
      TransitionComponent={Fade}
      transitionDuration={200}
      PaperProps={{
        elevation: 0,
        sx: {
          borderRadius: 3,
          minWidth: 380,
          maxWidth: 420,
          overflow: "visible",
          border: "1px solid",
          borderColor: "divider",
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            backdropFilter: "blur(4px)",
          },
        },
      }}
    >
      <DialogContent sx={{ pt: 4, pb: 2, px: 4, textAlign: "center" }}>
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            backgroundColor: "warning.main",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 20px",
            boxShadow: "0 4px 14px rgba(237, 108, 2, 0.3)",
          }}
        >
          <WarningAmberIcon sx={{ fontSize: 32, color: "white" }} />
        </Box>

        <Typography
          id="inactivity-redirect-dialog-title"
          variant="h5"
          component="h2"
          sx={{ fontWeight: 600, mb: 1.5, color: "text.primary" }}
        >
          Session Ended
        </Typography>

        <Typography
          id="inactivity-redirect-dialog-description"
          variant="body1"
          sx={{ color: "text.secondary", lineHeight: 1.6, mb: 1 }}
        >
          {message}
        </Typography>

        <Typography
          variant="body2"
          sx={{ color: "text.secondary", lineHeight: 1.6 }}
        >
          Your editing session was automatically ended to protect your work. Any
          unsaved changes were preserved.
        </Typography>
      </DialogContent>

      <DialogActions
        sx={{
          px: 4,
          pb: 4,
          pt: 2,
          flexDirection: "column",
          alignItems: "stretch",
        }}
      >
        <Button
          onClick={onAcknowledge}
          variant="contained"
          color="primary"
          fullWidth
          disableElevation
          sx={{
            py: 1.25,
            borderRadius: 2,
            textTransform: "none",
            fontSize: "0.95rem",
            fontWeight: 600,
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              backgroundColor: "primary.dark",
              transform: "translateY(-2px)",
              boxShadow: "0 4px 12px rgba(25, 118, 210, 0.4)",
            },
            "&:active": {
              transform: "translateY(0)",
              boxShadow: "0 2px 6px rgba(25, 118, 210, 0.3)",
            },
          }}
          autoFocus
        >
          I Understand
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InactivityRedirectModal;
